import { FormikHelpers } from 'formik';
import { observable, action } from 'mobx';
import {
  API_FUNCTION_TYPE,
  API_FUNCTION_TYPE_DEPOSIT,
  API_ACCOUNT_FINANCE_DEPOSIT,
  API_ACCOUNT_FINANCE_SAVE_DEPOSIT,
  API_ACCOUNT_FINANCE_DEPOSITS,
  API_ACCOUNT_FINANCE_DEPOSIT_SINGLE,
  API_ACCOUNT_FINANCE_DEPOSIT_REGULAR,
  API_ACCOUNT_FINANCE_DEPOSIT_EMPLOYER,
  API_ACCOUNT_FINANCE_DEPOSIT_EMPLOYERALLOWED,
  API_ACCOUNT_DETAIL_DEPOSITS,
  API_ACCOUNT_DETAIL_DEPOSIT_SINGLE,
  API_ACCOUNT_DETAIL_DEPOSIT_REGULAR,
  API_ACCOUNT_DETAIL_DEPOSIT_EMPLOYER,
  API_ACCOUNT_DETAIL_DEPOSIT_EMPLOYERALLOWED,
  API_ACCOUNT_DETAIL_DEPOSIT,
  API_ACCOUNT_DETAIL_SAVE_DEPOSIT,
  API_ACCOUNT_WITHDRAW_DEPOSIT,
  API_ACCOUNT_WITHDRAW_SAVE_DEPOSIT,
  API_ACCOUNT_DETAIL_DEPOSIT_REGULAR_CALC,
  API_ACCOUNT_TRANSFER_SAVE_DEPOSIT,
  API_ACCOUNT_TRANSFER_DEPOSIT
} from 'api/endpoints';
import { FormStore } from 'stores/formStore';
import { RootStore } from 'stores/rootStore';
import { Deposit, DepositsData, DepositType } from './model';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';

export class DepositsStore extends FormStore<Deposit> {
  accountId: string;
  endpoint: API_FUNCTION_TYPE;
  endpointDeposit: API_FUNCTION_TYPE_DEPOSIT;
  endpointSaveDeposit: API_FUNCTION_TYPE;
  endpointSingle: API_FUNCTION_TYPE;
  endpointRegular: API_FUNCTION_TYPE;
  endpointEmployer: API_FUNCTION_TYPE;
  endpointEmployerAllowed: API_FUNCTION_TYPE;
  @observable allDeposits?: DepositsData;

  @observable calculating = false;
  @observable calcResult: string | null = null;
  @observable calcValue: number | null = null;

  constructor(rootStore: RootStore, accountId: string, viewMode?: ViewMode) {
    super(rootStore);
    this.accountId = accountId;

    switch (viewMode) {
      case ViewMode.DETAIL:
        this.endpoint = API_ACCOUNT_DETAIL_DEPOSITS;
        this.endpointSaveDeposit = API_ACCOUNT_DETAIL_SAVE_DEPOSIT;
        this.endpointDeposit = API_ACCOUNT_DETAIL_DEPOSIT;
        this.endpointSingle = API_ACCOUNT_DETAIL_DEPOSIT_SINGLE;
        this.endpointRegular = API_ACCOUNT_DETAIL_DEPOSIT_REGULAR;
        this.endpointEmployer = API_ACCOUNT_DETAIL_DEPOSIT_EMPLOYER;
        this.endpointEmployerAllowed = API_ACCOUNT_DETAIL_DEPOSIT_EMPLOYERALLOWED;
        break;

      case ViewMode.WITHDRAW:
        this.endpoint = API_ACCOUNT_FINANCE_DEPOSITS; // not used in withdrawal
        this.endpointSaveDeposit = API_ACCOUNT_WITHDRAW_SAVE_DEPOSIT;
        this.endpointDeposit = API_ACCOUNT_WITHDRAW_DEPOSIT;
        this.endpointSingle = API_ACCOUNT_FINANCE_DEPOSIT_SINGLE; // not used in withdrawal
        this.endpointRegular = API_ACCOUNT_FINANCE_DEPOSIT_REGULAR; // not used in withdrawal
        this.endpointEmployer = API_ACCOUNT_DETAIL_DEPOSIT_REGULAR; // not used in withdrawal
        this.endpointEmployerAllowed = API_ACCOUNT_DETAIL_DEPOSIT_EMPLOYERALLOWED; // not used in withdrawal
        break;

      case ViewMode.TRANSFER:
        this.endpoint = API_ACCOUNT_FINANCE_DEPOSITS; // not used in transfer
        this.endpointSaveDeposit = API_ACCOUNT_TRANSFER_SAVE_DEPOSIT;
        this.endpointDeposit = API_ACCOUNT_TRANSFER_DEPOSIT;
        this.endpointSingle = API_ACCOUNT_FINANCE_DEPOSIT_SINGLE; // not used in transfer
        this.endpointRegular = API_ACCOUNT_FINANCE_DEPOSIT_REGULAR; // not used in transfer
        this.endpointEmployer = API_ACCOUNT_DETAIL_DEPOSIT_REGULAR; // not used in transfer
        this.endpointEmployerAllowed = API_ACCOUNT_DETAIL_DEPOSIT_EMPLOYERALLOWED; // not used in transfer
        break;

      default:
        this.endpoint = API_ACCOUNT_FINANCE_DEPOSITS;
        this.endpointSaveDeposit = API_ACCOUNT_FINANCE_SAVE_DEPOSIT;
        this.endpointDeposit = API_ACCOUNT_FINANCE_DEPOSIT;
        this.endpointSingle = API_ACCOUNT_FINANCE_DEPOSIT_SINGLE;
        this.endpointRegular = API_ACCOUNT_FINANCE_DEPOSIT_REGULAR;
        this.endpointEmployer = API_ACCOUNT_FINANCE_DEPOSIT_EMPLOYER;
        this.endpointEmployerAllowed = API_ACCOUNT_FINANCE_DEPOSIT_EMPLOYERALLOWED;
        break;
    }
  }

  deleteDeposit = async (type: DepositType) => {
    this.rootStore.stores.uiStore.setLoading(true);
    let ep = '';
    switch (type) {
      case DepositType.SINGLE:
        ep = this.endpointSingle(this.accountId);
        break;
      case DepositType.REGULAR:
        ep = this.endpointRegular(this.accountId);
        break;
      case DepositType.EMPLOYER:
        ep = this.endpointEmployer(this.accountId);
        break;
    }
    await this.rootStore.stores.apiStore.delete(ep);
    this.rootStore.stores.uiStore.setLoading(false);
  };

  toggleEmployerAllowed = async (allow: boolean) => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.rootStore.stores.apiStore.post(this.endpointEmployerAllowed(this.accountId), { value: allow });
    //await this.fetchDeposits();
    this.rootStore.stores.uiStore.setLoading(false);
  };

  deleteFutureDeposit = async (depositId: string) => {
    this.rootStore.stores.uiStore.setLoading(true);
    await this.rootStore.stores.apiStore.delete(this.endpointDeposit(this.accountId, depositId));
    this.rootStore.stores.uiStore.setLoading(false);
  };

  fetchDeposits = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<DepositsData>(this.endpoint(this.accountId));
    if (response?.data) {
      this.allDeposits = response.data;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  handleSubmit = async (values: Deposit, formikHelpers: FormikHelpers<Deposit>, type: DepositType) => {
    let endpoint = '';
    switch (type) {
      case DepositType.SINGLE:
        endpoint = this.endpointSingle(this.accountId);
        break;
      case DepositType.REGULAR:
        endpoint = this.endpointRegular(this.accountId);
        break;
      case DepositType.FUTURE:
        endpoint = this.endpointSaveDeposit(this.accountId);
        break;
      case DepositType.EMPLOYER:
        endpoint = this.endpointEmployer(this.accountId);
        break;
    }
    return await this.submitData(endpoint, values, {
      formikHelpers,
      reset: false,
      validationPrefixDepth: 1,
      singleValueResponse: true,
    });
  };

  @action
  calculateRegularDeposit = async () => {
    this.calculating = true;
    this.calcResult = null;
    const response = await this.rootStore.stores.apiStore.post<{ value: { value: number } }>(
      API_ACCOUNT_DETAIL_DEPOSIT_REGULAR_CALC(this.accountId), null, true, true
    );
    if (response?.status === 204) {
      this.calcResult = 'deposits_calc_depositok';
    } else if (response?.data?.value) {
      this.calcValue = response?.data?.value.value;
      this.calcResult = 'deposits_calc_recommended';
    } else {
      this.calcResult = 'deposits_calc_error';
    }
    this.calculating = false;
  };

  @action
  resetCalculatedDeposit = () => {
    this.calcResult = null;
  }

}
